import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import '@/App.css';
import Landing from './pages/Landing';
import Login from './pages/Login';
import AdminEditUser from './pages/AdminEditUser';
import SignupWizard from './pages/SignupWizard';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import GoogleCallback from './pages/GoogleCallback';
import ApplyNow from './pages/ApplyNow';
import ForFunders from './pages/ForFunders';
import ForLawFirms from './pages/ForLawFirms';
import FAQ from './pages/FAQ';
import NotFound from './pages/NotFound';
import Contact from './pages/Contact';
import CaseTypePage from './pages/CaseTypePage';
import { CASE_TYPE_PAGES } from './data/caseTypePages';
import AttorneyInvite from './pages/AttorneyInvite';
import ReferralClaim from './pages/ReferralClaim';
import SetupAccount from './pages/SetupAccount';
import InviteAccept from './pages/InviteAccept';
import ClientDashboard from './pages/ClientDashboard';
import LawyerDashboard from './pages/LawyerDashboard';
import FunderDashboard from './pages/FunderDashboard';
import AdminDashboard from './pages/AdminDashboard';
import GrowthPartnerDashboard from './pages/GrowthPartnerDashboard';
import { Toaster } from 'sonner';
import {
  extractCampaignCode,
  extractRefCode,
  shouldCaptureReferral,
  stripReferralParams,
} from '@/lib/referralCapture';
import { apiPost } from '@/lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Record a referral click for `search`, then drop the parameters from the URL.
 *
 * Module-level rather than inline in the effect because two things call it:
 * the location effect below, and a bfcache restore, which never remounts.
 *
 * Stripping `ref` is what makes this safe to call more than once — a second
 * call for the same visit finds no code and returns before posting. That is
 * also the diagnostic: a `ref` still sitting in the address bar means this
 * function did not run, because the strip happens whether or not the POST
 * succeeds.
 */
const captureReferral = (pathname, search, hash) => {
  const code = extractRefCode(search);
  if (!code || !shouldCaptureReferral(pathname)) return;

  // Spec §6.1 step 2: a visitor already authenticated as a lawyer has, if
  // anything, a permanent assignment already governing them — the server
  // refuses to attribute an existing lawyer regardless, so this just
  // avoids the pointless request. Deliberately shallow: no decoding the
  // token or fetching the user, just "is anyone logged in". Some privacy
  // modes throw on localStorage access, so this must not take the effect
  // down with it.
  let hasToken = false;
  try {
    hasToken = !!window.localStorage.getItem('access_token');
  } catch {
    hasToken = false;
  }
  if (hasToken) return;

  // Fire and forget. Attribution is secondary to the page working, so
  // nothing here may throw, block render, or surface anything to the
  // visitor — the endpoint answers 200 even for an invalid code.
  apiPost('/referral/click', {
    code,
    campaign: extractCampaignCode(search),
    path: pathname,
  }).catch(() => {});

  // Drop `ref` and `campaign` from the visible URL, preserving every other parameter.
  // Same replaceState approach GoogleCallback.js uses to strip `code`.
  const cleaned = stripReferralParams(search);
  window.history.replaceState({}, '', `${pathname}${cleaned}${hash || ''}`);
};

export function AppRouter() {
  const location = useLocation();

  useEffect(() => {
    captureReferral(location.pathname, location.search, location.hash);
  }, [location.pathname, location.search, location.hash]);

  // A page restored from the back/forward cache is resumed, not remounted, so
  // the effect above never runs for it and the visit goes unattributed. This
  // is the ordinary path for a scanned QR code on a phone that already has the
  // site open in a tab: iOS Safari hands back the cached page and `ref` is
  // still in the URL, unread. Reported from production against a printed
  // one-pager; closing every tab was what made attribution start working.
  //
  // Reads window.location rather than the router's, because on a restore the
  // router never re-derived it. Only `persisted` restores qualify: a normal
  // load fires pageshow too, and acting on that would double-count every
  // fresh visit alongside the mount effect above.
  useEffect(() => {
    const onPageShow = (event) => {
      if (!event.persisted) return;
      captureReferral(window.location.pathname, window.location.search, window.location.hash);
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/apply" element={<ApplyNow />} />
      {/* Legacy path left over from the original page builder. Kept as a redirect so
          existing links, ads, and bookmarks do not break. */}
      <Route path="/apply-now-1" element={<Navigate to="/apply" replace />} />
      <Route path="/for-funders" element={<ForFunders />} />
      <Route path="/for-law-firms" element={<ForLawFirms />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/contact" element={<Contact />} />
      {CASE_TYPE_PAGES.map((page) => (
        <Route
          key={page.slug}
          path={page.path}
          element={<CaseTypePage slug={page.slug} />}
        />
      ))}
      <Route path="/attorney-invite" element={<AttorneyInvite />} />
      <Route path="/referral" element={<ReferralClaim />} />
      <Route path="/setup-account" element={<SetupAccount />} />
      <Route path="/invite/:token" element={<InviteAccept />} />
      <Route path="/login" element={<Login />} />
      <Route path="/auth/google/callback" element={<GoogleCallback />} />
      {/* Admins sign in on the normal login screen, which already routes an
          admin-role user to /admin. The separate page is retired, but the URL
          stays registered as a redirect — it is bookmarked and saved in
          password managers, and a redirect turns "the page moved" into a
          working login rather than a 404. Same pattern as /apply-now-1. */}
      <Route path="/admin-login" element={<Navigate to="/login" replace />} />
      <Route path="/signup" element={<SignupWizard />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/client/*" element={<ProtectedRoute role="client"><ClientDashboard /></ProtectedRoute>} />
      <Route path="/lawyer/*" element={<ProtectedRoute role="lawyer"><LawyerDashboard /></ProtectedRoute>} />
      <Route path="/funder/*" element={<ProtectedRoute role="funder"><FunderDashboard /></ProtectedRoute>} />
      <Route path="/admin/*" element={<ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>} />
      <Route path="/growth-partner/*" element={<ProtectedRoute role="growth_partner"><GrowthPartnerDashboard /></ProtectedRoute>} />
      <Route path="/admin/users/:userId/edit" element={<ProtectedRoute role="admin"><AdminEditUser /></ProtectedRoute>} />
      {/* Catch-all. Keep last: an unmatched URL previously rendered a blank page. */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function ProtectedRoute({ children, role }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const location = useLocation();

  useEffect(() => {
    // Skip auth check when a page navigated here with the user in hand —
    // GoogleCallback, SignupWizard, ApplyNow and InviteAccept all do.
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      const token = localStorage.getItem('access_token');
      try {
        // Try to use stored token for auth
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

        const response = await fetch(`${API}/auth/me`, {
          credentials: 'include',
          headers: headers
        });

        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
          setIsAuthenticated(true);
          return;
        }

        // Only force logout on real auth failures (401/403).
        // For transient errors (5xx, network) keep the user logged in
        // if they have a token; the next API call will recover or surface the error.
        if (response.status === 401 || response.status === 403) {
          localStorage.removeItem('access_token');
          setIsAuthenticated(false);
          return;
        }

        console.warn(`[Auth] /auth/me returned ${response.status} — keeping session`);
        if (token) {
          // Trust the existing token; pages will refetch user-specific data themselves.
          setUser(null);
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }
      } catch (error) {
        // Network / fetch failure — do NOT remove the token. Treat as transient.
        console.warn('[Auth] /auth/me network error — keeping session:', error?.message);
        if (token) {
          setUser(null);
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }
      }
    };
    
    checkAuth();
  }, [location.state]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f8fafc' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (role && user?.role !== role) {
    // Paralegals share the Lawyer dashboard (with Team Members nav hidden).
    if (role === 'lawyer' && user?.role === 'paralegal') {
      return children;
    }
    return <Navigate to="/login" replace />;
  }

  return children;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
